/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
/* eslint-disable jsx-a11y/anchor-is-valid */

import { BodyM, Caption, colorVariants } from '@casavo/base-ui'
import useTranslation from 'next-translate/useTranslation'
import getConfig from 'next/config'
import React from 'react'
import ImageGallery from 'react-image-gallery'
import { LazyLoadImage } from 'react-lazy-load-image-component'

import ListingDetailLink from '@/components/Link/ListingDetailLink'
import { sprinkles } from '@/design-lib/style-theme/sprinkles.css'
import { xsRadius } from '@/shared-styles/common-variables'
import { Listings_Detail } from '@/types/cate-graphql-generated-types'
import { formatPrice } from '@/utils/format-currency'
import getTranslatedTitle from '@/utils/get-translated-title'
import { handleListingsMapClick, sendSupportEventWithListingId } from '@/utils/tracking/eventsHandlers'

import Icon from '../Icon'

import {
  cardBody,
  cardCasavoLogo,
  cardData,
  cardDataValue,
  cardGrid,
  cardImage,
  cardLink,
  cardTitle,
} from './style.css'

type Props = {
  aggregateLength?: number
  dataTestId?: string
  isAggregateListOpen: boolean
  isOnDetail?: boolean
  property: Listings_Detail
}

const ListingCardMapMobile = React.memo<Props>(
  ({ aggregateLength, dataTestId, isAggregateListOpen, isOnDetail = false, property }: Props) => {
    const { lang } = useTranslation()
    const imageHeight = 100

    const images = property.photos.slice(0, 8).map((photo) => ({
      original: `${photo.url}?auto=compress,format&fit=crop&ar=1:1&h=${imageHeight}`,
      primary: photo.isPrimary,
    }))

    const {
      publicRuntimeConfig: { casavoUiCdn },
    } = getConfig()

    return (
      <ListingDetailLink cityValue={property.detail.city} slug={property.slug} zoneSlug={property.detail.zone?.slug}>
        <a
          className={cardLink({
            aggregateLengthLessThan3: aggregateLength < 3,
            isAggregateListOpen,
            isOnDetail,
          })}
          data-testid={dataTestId}
          onClick={() => {
            handleListingsMapClick('ListingCard')
            sendSupportEventWithListingId(property.id, true)
          }}
        >
          <div className={cardGrid({ isOnDetail })}>
            <div className={cardImage}>
              {property.contactInfo?.ownedByCasavo && (
                <div
                  className={cardCasavoLogo}
                  style={{
                    backgroundImage: `url(${casavoUiCdn}/logo/main-symbol-black.svg)`,
                  }}
                />
              )}
              <ImageGallery
                disableKeyDown={true}
                items={images}
                renderItem={(item) => {
                  return (
                    <LazyLoadImage
                      height={imageHeight}
                      src={item.original}
                      style={{
                        borderBottomLeftRadius: xsRadius,
                        borderTopLeftRadius: xsRadius,
                        height: imageHeight,
                      }}
                      visibleByDefault={false}
                      width={'100%'}
                    />
                  )
                }}
                renderLeftNav={() => null}
                renderRightNav={() => null}
                showBullets={false}
                showFullscreenButton={false}
                showPlayButton={false}
                showThumbnails={false}
                slideDuration={450}
              />
            </div>
            <div className={cardBody}>
              <p className={cardTitle}>{getTranslatedTitle(property.detail?.titles, lang)}</p>
              <div
                className={sprinkles({
                  alignItems: 'center',
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                })}
              >
                <div className={cardData}>
                  <div className={cardDataValue}>
                    <span className={sprinkles({ marginRight: 'm' })}>{property.detail.commercialArea}</span>
                    <Caption as="span" className={sprinkles({ marginLeft: 'xs' })} color="greyscale500">
                      m&sup2;
                    </Caption>
                  </div>
                </div>

                <div className={sprinkles({ alignItems: 'center', display: 'flex', gap: 's' })}>
                  {property.contactInfo?.ownedByCasavo && (
                    <Icon.Check aria-hidden="true" className={colorVariants.orange600} />
                  )}

                  {property.detail.sellingPrices && (
                    <BodyM strong>{formatPrice(property.detail.sellingPrices, lang)}</BodyM>
                  )}
                </div>
              </div>
            </div>
          </div>
        </a>
      </ListingDetailLink>
    )
  }
)

ListingCardMapMobile.displayName = 'ListingCardMapMobile'

export default ListingCardMapMobile
